import { useState, type CSSProperties } from "react";
import { Banner, Btn, Dropdown, FilterToolbar, Icon, IconBtn, PageState, SegmentedControl, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { Screen, PageHeader } from "../../shell/chrome";
import { StatTile } from "../../shell/composites";
import { EventsInlineTable, EVENT_STATUS } from "./EventsInlineTable";
import { EventsInlineCalendar } from "./EventsInlineCalendar";
import { useEvents } from "./useEvents";
import { useViewMode, ViewToggle, useRowSelection, BulkActionBar, CARD_GRID, tabExport, eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "./eventSectionCounts";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";
import type { UIEvent } from "../../lib/events-adapter";
import { useT, t as i18nT } from "../../lib/i18n";
import { friendlyDate, useDateTimeVersion } from "../../lib/datetime";

/**
 * Events list — verbatim port of the prototype's Events page (table view,
 * stat tiles, filters, search, sort). The ONLY change is the data source:
 * `useEvents()` fetches via the typed api-client → adapter; the existing
 * client-side filter/sort/search runs over that array, byte-for-byte.
 *
 * Interim (flagged): the inline Calendar view + the create split-button +
 * the section tabs port next; approvals sort returns 0 until that layer wires.
 */

// Add Event — opens the Journey designer on a NEW Event journey (2026-08-25):
// `?new=event` tells `newjourneys` to pick the Event template and open the
// designer, instead of dropping the user on the journeys list to choose again.
function CreateEventSplitBtn({ label = "Add Event", size = "md", variant = "primary", full = false }: { label?: string; size?: "sm"|"md"|"lg"; variant?: "primary"|"secondary"; full?: boolean }) {
  const nav = useNav();
  return (
    <Btn size={size} variant={variant} icon={<Icon.plus size={size === "sm" ? 12 : 14} />} style={full ? { width: "100%" } : undefined} onClick={() => nav.go("newjourneys", { new: "event" })}>{label}</Btn>
  );
}

// (formatToday removed 2026-07-19 — hand-rolled English-only date violated the
// lib/datetime standing rule and didn't localize; subtitle now uses friendlyDate.)

// ui-config — static per-column default sort-direction map
const HEADER_DEFAULT_DIR: Record<string, "asc" | "desc"> = { title: "asc", when: "asc", location: "asc", host: "asc", registered: "desc", capacity: "desc", status: "asc", approval: "asc", newest: "desc", date: "asc" };
// ui-config — column label map
const COLUMN_LABELS: Record<string, string> = { title: "Event name", when: "Date", location: "Location", host: "Host", approval: "Approval stage" };
// ui-config — sort label map
const SORT_LABELS: Record<string, string> = { date: "Nearest date", registered: "Most registered", capacity: "Capacity %", newest: "Recently added", status: "Status" };
/* STATUS_ORDER deleted 2026-08-19 (§3): a second copy of the status vocabulary the
   table's pill also declared. Both now read EVENT_STATUS. */

export default function EventsList() {
  const nav = useNav();
  const t = useT(); // i18n pilot (2026-07-19): keys limited to the shipped base catalog — unmatched copy stays literal (REQ-043b)
  useDateTimeVersion(); // subtitle date re-formats live on locale/tz change
  const { events: EVENTS, total: serverTotal, loading, error, reload } = useEvents();
  // Real section-tab totals (templates/tickets/landing) — the shared hook reads
  // the op-backed stores (listEventTemplates / listTicketTypes / listLandingPages),
  // same counts every event-section screen shows.
  const sectionCounts = useEventSectionCounts();
  const [view, setView] = useState<"table" | "calendar">("table");
  const [filter, setFilter] = useState("all");
  const [locFilter, setLocFilter] = useState<string | null>(null);
  const [hostFilter, setHostFilter] = useState<string | null>(null);
  // Persistence is the toolbar's job now: `persistKey="events.list"` writes
  // `jrni.filters.events.list.v1` as `{ query, selected }` and hydrates through these
  // same setters (§6). The old key is not migrated — it held a search string, not work.
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const onHeaderSort = (key: string) => {
    if (sort === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setSortDir(HEADER_DEFAULT_DIR[key] || "asc"); }
  };

  /* Folder organization — the shared Folder Library shell (family "events"):
     mounted, not copied, so the folders UX is identical to Messages/Questions/
     Surveys/Promos. Folders group events by university department (operator,
     2026-08-13); scoping filters the list AHEAD of the status/location/host/
     search filters below. Calendar view is left un-scoped deliberately —
     folders are a list-organization tool. */
  const lib = useFolderLibrary({
    family: "events",
    ids: EVENTS.map((e) => e.id),
    canWrite: can("events.write"),
    allLabel: t("events.list.allEvents", "All events"),
    searchAllLabel: t("collections.searchEverywhereEvents", "Search all events"),
    query,
    failTitle: t("events.list.couldnTUpdateEvent", "Couldn't update event"),
    onAfterPurge: () => reload(),
  });
  const poolSet = new Set(lib.pool);

  const matches = (e: UIEvent) => {
    if (!poolSet.has(e.id)) return false;
    if (filter !== "all" && e.status !== filter) return false;
    if (locFilter && !e.location.toLowerCase().includes(locFilter.toLowerCase())) return false;
    if (hostFilter && e.host !== hostFilter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const hay = `${e.title} ${e.host} ${e.location} ${e.template || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const filtered = EVENTS.filter(matches);
  const _evTime = (e: UIEvent) => { const t = Date.parse(e.raw?.startsAt || e.date || ""); return Number.isNaN(t) ? Infinity : t; };
  const _cmp = (a: UIEvent, b: UIEvent) => {
    switch (sort) {
      case "title": return (a.title || "").localeCompare(b.title || "");
      case "location": return (a.location || "").localeCompare(b.location || "");
      case "host": return (a.host || "").localeCompare(b.host || "");
      case "when": return _evTime(a) - _evTime(b);
      case "registered": return (a.registered || 0) - (b.registered || 0);
      case "capacity": return ((a.capacity ? a.registered / a.capacity : 0)) - ((b.capacity ? b.registered / b.capacity : 0));
      case "status": return (EVENT_STATUS[a.status]?.order ?? 9) - (EVENT_STATUS[b.status]?.order ?? 9);
      default: return 0;
    }
  };
  const sorted = [...filtered].sort((a, b) => { if (sort === "date") return 0; const base = _cmp(a, b); return sortDir === "desc" ? -base : base; });

  const totalRegistered = EVENTS.reduce((s, e) => s + (e.registered || 0), 0);
  const totalCapacity = EVENTS.reduce((s, e) => s + (e.capacity || 0), 0);
  const capacityPct = totalCapacity > 0 ? Math.round((totalRegistered / totalCapacity) * 100) : 0;
  const counts = {
    all: EVENTS.filter((e) => e.status !== "past").length,
    live: EVENTS.filter((e) => e.status === "live").length,
    upcoming: EVENTS.filter((e) => e.status === "upcoming").length,
    draft: EVENTS.filter((e) => e.status === "draft").length,
    canceled: EVENTS.filter((e) => e.status === "canceled").length,
    past: EVENTS.filter((e) => e.status === "past").length,
  };
  const hosts = [...new Set(EVENTS.map((e) => e.host))];
  const locs = [...new Set(EVENTS.map((e) => e.location.split(" · ")[0]))];
  const clearFilters = () => { setFilter("all"); setLocFilter(null); setHostFilter(null); setQuery(""); };
  const filtersActive = filter !== "all" || locFilter || hostFilter || query;

  const sectionTabs = eventsSectionTabs(nav, "events", sectionCounts);

  const contentWrap: CSSProperties = view === "calendar"
    ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", padding: "16px 24px 20px" }
    : { flex: 1, overflow: "auto", padding: "0 24px 28px" };

  return (
    <Screen rail="events" tabs={sectionTabs} activeTab="events">
      <PageHeader
        title={t("events.list.title", "Events")}
        subtitle={t("events.list.friendlydateNewDateFilteredLengthOfEventsLengthShownFiltersactiveFilters.icu", { date: friendlyDate(new Date()), shown: filtered.length, total: serverTotal, filters: filtersActive ? "active" : "none" })}
        breadcrumb={null}
        actions={<>
          <IconBtn label={t("events.list.refreshEvents", "Refresh events")} onClick={reload}><Icon.refresh size={16} /></IconBtn>
          <SegmentedControl<"table" | "calendar">
            size="md"
            ariaLabel={t("events.list.view", "View")}
            value={view}
            onChange={setView}
            options={[
              { value: "table", label: t("events.list.table", "Table") },
              { value: "calendar", label: t("events.list.calendar", "Calendar") },
            ]}
          />
          {can("reports.export") ? <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => tabExport("events", sorted.map((e) => ({ title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, registered: e.registered, capacity: e.capacity })))}>{t("common.actions.export", "Export")}</Btn> : null}
          {can("events.write") ? <CreateEventSplitBtn label={t("events.list.addEvent", "Add Event")} /> : null}
        </>}
      />

      {view === "calendar" ? (
        <div style={contentWrap}>
          {loading || error ? (
            <PageState noun={t("events.sectionState.nounEvents", "events")} icon={<Icon.tags size={20} />}
              loading={loading} error={error} onRetry={reload}
              empty={false} filtered={false} onClearFilters={clearFilters}
              action={can("events.write") ? <CreateEventSplitBtn label={t("events.list.addEvent", "Add Event")} /> : null} />
          ) : (
            <EventsInlineCalendar events={sorted} fill onOpen={(ev) => nav.openEvent(ev)} onOpenWeek={() => nav.go("calendar.week")} />
          )}
        </div>
      ) : (
        <FolderShellFrame lib={lib}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            <StatTile label={t("events.status.upcoming", "Upcoming")} value={counts.live + counts.upcoming} icon={<Icon.calendar size={14} />} accent={T.p1} active={filter === "upcoming"} onClick={() => setFilter("upcoming")} />
        <StatTile label={t("events.list.liveNow", "Live now")} value={counts.live} icon={<Icon.eye size={14} />} accent={T.green1} active={filter === "live"} onClick={() => setFilter("live")} />
        <StatTile label={t("events.list.totalRegistered", "Total registered")} value={totalRegistered.toLocaleString()} delta={`${capacityPct}% of capacity`} deltaPos icon={<Icon.users size={14} />} accent={T.p1} />
        <StatTile label={t("events.list.draftsPending", "Drafts pending")} value={counts.draft} icon={<Icon.edit size={14} />} accent={T.orange1} active={filter === "draft"} onClick={() => setFilter("draft")} />
          </div>
          {/* ONE filter surface (§4). It renders ABOVE the state branch, never inside
              it: losing the filter UI in the one state where the user most needs to
              change a filter is the most common bug in a list screen. Counting stays
              client-side and stays visibly wrong past page one — the toolbar does no
              counting; see the server-aggregates drain row. */}
          <FilterToolbar
            testid="events-filters"
            persistKey="events.list"
            density="compact"
            query={query}
            onQueryChange={setQuery}
            searchPlaceholder={t("events.list.searchPlaceholder")}
            searchAriaLabel={t("events.list.searchEvents", "Search events")}
            resultCount={{ shown: filtered.length, total: serverTotal }}
            facets={[
              { id: "location", label: t("events.eventsList.anyLocation", "Any location"), options: locs.map((l) => ({ value: l, label: l })) },
              { id: "host", label: t("events.eventsList.anyHost", "Any host"), options: hosts.map((h) => ({ value: h, label: h })) },
            ]}
            selected={{ location: locFilter ?? undefined, host: hostFilter ?? undefined }}
            onToggle={(facetId, value) => {
              if (facetId === "location") setLocFilter((prev) => (prev === value ? null : value));
              else setHostFilter((prev) => (prev === value ? null : value));
            }}
            onClearFacet={(facetId) => { if (facetId === "location") setLocFilter(null); else setHostFilter(null); }}
            onClearAll={clearFilters}
            leading={
              <SegmentedControl<string>
                size="sm"
                ariaLabel={t("events.list.statusFilter", "Status filter")}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: "all", label: i18nT("events.list.allCountsAll.icu", { value0: counts.all }) },
                  { value: "live", label: t("events.list.tEventsStatusLiveCountsLive.icu", { value0: t("events.status.live", "Live"), value1: counts.live }) },
                  { value: "upcoming", label: t("events.list.tEventsStatusUpcomingCountsUpcoming.icu", { value0: t("events.status.upcoming", "Upcoming"), value1: counts.upcoming }) },
                  { value: "draft", label: i18nT("events.list.draftsCountsDraft.icu", { value0: counts.draft }) },
                  { value: "canceled", label: i18nT("events.list.cancelledCountsCanceled.icu", { value0: counts.canceled }) },
                ]}
              />
            }
            actions={
              /* Sort is NOT a filter: it sits outside the facet group, in a real
                 Dropdown, and does not count toward "filters active". */
              <Dropdown<string>
                size="sm"
                aria-label={t("events.list.sortBy", "Sort by")}
                value={sort}
                onChange={(key) => { setSort(key); setSortDir(HEADER_DEFAULT_DIR[key] || "asc"); }}
                options={Object.keys(SORT_LABELS).map((k) => ({ value: k, label: SORT_LABELS[k] }))}
              />
            }
          />
          <FolderShellBars lib={lib} />
          <Banner tone="info">
            <b>{t("events.list.title", "Events")}</b> {t("events.list.areEverythingLiveUpcomingAndInDraftUse", "are everything live, upcoming, and in draft. Use")}{" "}<b>{t("events.list.addEvent", "Add Event")}</b> {t("events.list.toStartFromATemplateAiOrScratchEventsAutomaticallyMoveTo", "to start from a template, AI, or scratch — events automatically move to")}{" "}<span style={{ color: T.p1, fontWeight: 600, cursor: "pointer" }} onClick={() => nav.go("events.archived")}>{t("events.list.archive", "Archive")}</span> {t("events.list.onceTheyVeEnded", "once they've ended.")}</Banner>
          {loading || error || filtered.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <PageState noun={t("events.sectionState.nounEvents", "events")} icon={<Icon.tags size={20} />}
                loading={loading} error={error} onRetry={reload}
                empty={filtered.length === 0} filtered={!!filtersActive || lib.scope !== "all"}
                onClearFilters={() => { clearFilters(); lib.setScope("all"); }}
                action={can("events.write") ? <CreateEventSplitBtn label={t("events.list.addEvent", "Add Event")} /> : null} />
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <EventsInlineTable events={sorted.filter((e) => e.status !== "past")} sortKey={sort} sortDir={sortDir} onSort={onHeaderSort}
                folderChipsFor={lib.folderChips}
                foldersItemFor={can("events.write") ? lib.foldersMenuItem : undefined} />
            </div>
          )}
        </FolderShellFrame>
      )}
      <FolderShellOverlays lib={lib} />
    </Screen>
  );
}
